/**
 * ============================================================================
 * usePageData
 * ============================================================================
 * 
 * 페이지 데이터 로딩 및 캐시 관리
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 1. 전체 페이지 데이터를 한번에 로드 (단일 API 호출)
 * 2. 페이지 캐시 관리
 * 3. 주석 로딩
 * 
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 사용 예시
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * const {
 *   pageData,
 *   nextPageData,
 *   loading,
 *   error,
 *   lastPageIndex,
 *   pageCacheRef,
 *   initialLoadedRangeRef,
 *   loadAdditionalPage,
 *   addToCache,
 *   cleanupCache,
 * } = usePageData(bookUid, pageIndex, isProgressLoading, mapAnnotationsParagraphId);
 * 
 * ============================================================================
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { bookService, ApiPageData } from '../../../services/bookService';
import { annotationService } from '../../../services/annotationService';
import { Annotation } from '../../../types/book';

interface UsePageDataParams {
  bookUid: string | undefined;
  pageIndex: string | undefined;
  isProgressLoading: boolean;
  mapAnnotationsParagraphId: (annotations: Annotation[], components: any[]) => Annotation[];
  onAnnotationsLoaded?: (annotations: Annotation[]) => void;
}

export interface UsePageDataReturn {
  pageData: ApiPageData | null;
  nextPageData: ApiPageData | null;
  loading: boolean;
  error: string | null;
  lastPageIndex: number;
  pageCacheRef: React.MutableRefObject<Map<number, { page: ApiPageData; annotations: Annotation[] }>>;
  initialLoadedRangeRef: React.MutableRefObject<{ start: number; end: number }>;
  maxKnownPageRef: React.MutableRefObject<number>;
  loadAdditionalPage: (pageNum: number) => Promise<ApiPageData | null>;
  addToCache: (pageNum: number, data: { page: ApiPageData; annotations: Annotation[] }) => void;
  cleanupCache: (currentStartPage: number) => void;
  loadAllPagesInBackground: (uid: string, knownLastPageIndex: number, initialLoadedStartPage: number, initialLoadedEndPage: number) => Promise<void>;
  prefetchSurroundingPages: (uid: string, currentStartPage: number) => Promise<void>;
}

export const usePageData = ({
  bookUid,
  pageIndex,
  isProgressLoading,
  mapAnnotationsParagraphId,
  onAnnotationsLoaded,
}: UsePageDataParams): UsePageDataReturn => {
  // 페이지 데이터 상태
  const [pageData, setPageData] = useState<ApiPageData | null>(null);
  const [nextPageData, setNextPageData] = useState<ApiPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastPageIndex, setLastPageIndex] = useState<number>(0);
  
  // 페이지 캐시 (모든 페이지 저장 - 백그라운드에서 전체 로드)
  const pageCacheRef = useRef<Map<number, { page: ApiPageData; annotations: Annotation[] }>>(new Map());
  const MAX_CACHE_SIZE = 600;
  const initialLoadedRangeRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });
  const maxKnownPageRef = useRef<number>(0);
  
  // 전체 페이지 로드 완료 상태 추적
  const allPagesLoadedRef = useRef(false);
  // 페이지 데이터 로드 중복 실행 방지
  const loadingPageDataRef = useRef(false);
  const lastLoadedPageIndexRef = useRef<string | null>(null);
  
  // onAnnotationsLoaded 콜백을 ref로 저장 (무한 루프 방지)
  const onAnnotationsLoadedRef = useRef(onAnnotationsLoaded);
  useEffect(() => {
    onAnnotationsLoadedRef.current = onAnnotationsLoaded;
  }, [onAnnotationsLoaded]);
  
  // mapAnnotationsParagraphId를 ref로 저장 (의존성 배열에서 제거하기 위해)
  const mapAnnotationsParagraphIdRef = useRef(mapAnnotationsParagraphId);
  useEffect(() => {
    mapAnnotationsParagraphIdRef.current = mapAnnotationsParagraphId;
  }, [mapAnnotationsParagraphId]);
  
  // 단일 페이지 데이터와 주석을 로드하는 헬퍼 함수
  const loadSinglePageWithAnnotations = useCallback(async (
    uid: string,
    pageNum: number,
    components?: any[]
  ): Promise<{ page: ApiPageData | null; annotations: Annotation[] }> => {
    try {
      const page = await bookService.getPageInfo(uid, pageNum);
      const rawAnnotations: Annotation[] = [];
      const mappedAnnotations = mapAnnotationsParagraphIdRef.current(rawAnnotations, components || page.components);
      return { page, annotations: mappedAnnotations };
    } catch {
      return { page: null, annotations: [] };
    }
  }, [mapAnnotationsParagraphId]);
  
  // 캐시에 페이지 저장
  const addToCache = useCallback((pageNum: number, data: { page: ApiPageData; annotations: Annotation[] }) => {
    pageCacheRef.current.set(pageNum, data);
  }, []);
  
  // 캐시 정리 - 메모리 관리를 위해 600페이지 이상 캐시되면 가장 먼 페이지부터 제거
  const cleanupCache = useCallback((currentStartPage: number) => {
    if (pageCacheRef.current.size <= MAX_CACHE_SIZE) {
      return;
    }
    
    // 현재 페이지로부터의 거리 기준 정렬
    const sortedPages = Array.from(pageCacheRef.current.keys())
      .sort((a, b) => Math.abs(a - currentStartPage) - Math.abs(b - currentStartPage));
    
    // 가장 먼 페이지들 제거
    const keysToDelete = sortedPages.slice(MAX_CACHE_SIZE);
    
    if (keysToDelete.length > 0) {
      keysToDelete.forEach(key => pageCacheRef.current.delete(key));
      // console.log(`[Cache Cleanup] ${keysToDelete.length}개 페이지 캐시에서 제거`);
    }
  }, []);
  
  // 추가 페이지 로드 (콘텐츠가 부족할 때)
  const loadAdditionalPage = useCallback(async (pageNum: number): Promise<ApiPageData | null> => {
    if (!bookUid) return null;
    
    try {
      // 캐시에서 먼저 확인
      const cached = pageCacheRef.current.get(pageNum);
      if (cached) {
        // console.log(`[페이지 로드] 캐시에서 페이지 ${pageNum} 반환`);
        return cached.page;
      }
      
      // 캐시에 없으면 API 호출
      const result = await loadSinglePageWithAnnotations(bookUid, pageNum);
      if (result.page) {
        addToCache(pageNum, { page: result.page, annotations: result.annotations });
        if (onAnnotationsLoadedRef.current) {
          onAnnotationsLoadedRef.current(result.annotations);
        }
        return result.page;
      }
      return null;
    } catch (error) {
      // console.error(`추가 페이지 ${pageNum} 로드 실패:`, error);
      return null;
    }
  }, [bookUid, loadSinglePageWithAnnotations, addToCache]);
  
  // 전체 로드 방식에서는 프리페치가 불필요 (이미 모든 페이지가 캐시됨)
  const prefetchSurroundingPages = useCallback(async (_uid: string, _currentStartPage: number) => {
    if (allPagesLoadedRef.current) return;
  }, []);
  
  // 전체 로드 방식에서는 백그라운드 로드가 불필요 (초기 로드에서 전체 완료)
  const loadAllPagesInBackground = useCallback(async (
    _uid: string, 
    _knownLastPageIndex: number,
    _initialLoadedStartPage: number,
    _initialLoadedEndPage: number
  ) => {
    if (allPagesLoadedRef.current) return;
  }, []);
  
  // 전체 페이지 데이터 로드 (단일 API 호출로 모든 페이지를 한번에 가져옴)
  useEffect(() => {
    let isCancelled = false;
    
    if (isProgressLoading) {
      return;
    }
    
    if (!bookUid || !pageIndex) {
      setLoading(false);
      return;
    }
    
    const loadPageData = async () => {
      if (!bookUid || !pageIndex) {
        setError('책 UID와 페이지 인덱스가 필요합니다.');
        setLoading(false);
        return;
      }
    
      const pageNum = Math.max(1, parseInt(pageIndex, 10));
      const nextPageNum = pageNum + 1;
      
      // 캐시에서 데이터 확인 (이미 전체 로드된 경우)
      const cachedPage1 = pageCacheRef.current.get(pageNum);
      const cachedPage2 = pageCacheRef.current.get(nextPageNum);
      
      if (cachedPage1) {
        // console.log(`[Cache Hit] 페이지 ${pageNum} 캐시에서 로드${cachedPage2 ? `, ${nextPageNum}도 캐시됨` : ''}`);
        
        setPageData(cachedPage1.page);
        setNextPageData(cachedPage2?.page || null);
        if (onAnnotationsLoadedRef.current) {
          const annotations = [...cachedPage1.annotations];
          if (cachedPage2) {
            annotations.push(...cachedPage2.annotations);
          }
          onAnnotationsLoadedRef.current(annotations);
        }
        setLoading(false);
        loadingPageDataRef.current = false;
        lastLoadedPageIndexRef.current = pageIndex;
        return;
      }
      
      // 중복 실행 방지
      if (loadingPageDataRef.current && lastLoadedPageIndexRef.current === pageIndex) {
        return;
      }
      
      loadingPageDataRef.current = true;
      lastLoadedPageIndexRef.current = pageIndex;

      try {
        setLoading(true);
        setError(null);
        
        // 전체 페이지를 한번에 로드
        // console.log(`[Full Load] 전체 페이지 로드 시작 (bookUid: ${bookUid})`);
        
        const { pages, lastPageIndex: lastIdx } = await bookService.getAllPages(bookUid);
        
        if (isCancelled) return;
        
        if (lastIdx > 0) {
          setLastPageIndex(lastIdx);
          maxKnownPageRef.current = lastIdx;
        }
        
        const actualEndPage = pages.length > 0 ? Math.max(...pages.map(p => p.idx)) : 0;
        initialLoadedRangeRef.current = { start: 1, end: actualEndPage };
        
        // 모든 페이지를 캐시에 저장
        pages.forEach(page => {
          if (!pageCacheRef.current.has(page.idx)) {
            addToCache(page.idx, { page, annotations: [] });
          }
        });
        
        // 전체 로드 완료 표시
        allPagesLoadedRef.current = true;
        
        //console.log(`[Full Load] 전체 ${pages.length}페이지 캐시 완료 (마지막 페이지: ${lastIdx})`);
        
        // 현재 페이지 설정
        let page1Data = pages.find(p => p.idx === pageNum);
        if (!page1Data && pages.length > 0) {
          page1Data = pages[0];
          // console.log(`[Full Load] 요청한 페이지 ${pageNum}이 없어 첫 번째 페이지 ${page1Data.idx} 사용`);
        }
        const page2Data = pages.find(p => p.idx === (page1Data?.idx ?? pageNum) + 1);
        
        if (!page1Data) {
          throw new Error('페이지 데이터를 불러오는데 실패했습니다.');
        }
        
        setPageData(page1Data);
        setNextPageData(page2Data || null);
        setLoading(false);

        // 주석은 비동기 로드 (페이지 표시를 막지 않음)
        // 전체 page_no 범위 계산 (모든 페이지의 page_no 중 최소/최대)
        const allPageNos = pages.map(p => p.page_no).sort((a, b) => a - b);
        const startPageNo = allPageNos[0];
        const endPageNo = allPageNos[allPageNos.length - 1];

        if (startPageNo != null && endPageNo != null) {
          annotationService.getAnnotationsByPageRange(bookUid, startPageNo, endPageNo)
            .then(({ annotations: allAnnotations }) => {
              if (isCancelled || !page1Data) return;
              const p1 = page1Data;
              // page_no (DB 인덱스)로 필터링해야 함 (idx가 아닌 page_no)
              const ann1 = allAnnotations.filter(a => a.pageNumber === p1.page_no);
              const ann2 = page2Data ? allAnnotations.filter(a => a.pageNumber === page2Data.page_no) : [];
              const mappedAnn1 = mapAnnotationsParagraphIdRef.current(ann1, p1.components);
              const mappedAnn2 = page2Data ? mapAnnotationsParagraphIdRef.current(ann2, page2Data.components) : [];
              const allMappedAnnotations = [...mappedAnn1, ...mappedAnn2];
              if (onAnnotationsLoadedRef.current) {
                onAnnotationsLoadedRef.current(allMappedAnnotations);
              }
              const cached1 = pageCacheRef.current.get(pageNum);
              const cached2 = pageCacheRef.current.get(nextPageNum);
              if (cached1) cached1.annotations = mappedAnn1;
              if (cached2) cached2.annotations = mappedAnn2;
            })
            .catch((annError) => {
              // console.error('주석 로드 실패:', annError);
            });
        }
        
        loadingPageDataRef.current = false;
        
      } catch (err: any) {
        if (isCancelled) return;
        setError(err.message || '페이지 데이터를 불러오는 중 오류가 발생했습니다.');
        // console.error('페이지 데이터 로드 실패:', err);
        setLoading(false);
        loadingPageDataRef.current = false;
      }
    };

    loadPageData();
    
    return () => {
      isCancelled = true;
      loadingPageDataRef.current = false;
    };
  }, [bookUid, pageIndex, isProgressLoading]);
  
  return {
    pageData,
    nextPageData,
    loading,
    error,
    lastPageIndex,
    pageCacheRef,
    initialLoadedRangeRef,
    maxKnownPageRef,
    loadAdditionalPage,
    addToCache,
    cleanupCache,
    loadAllPagesInBackground,
    prefetchSurroundingPages,
  };
};
